// Package imports:
import React, { useState } from 'react';
// Component imports:
import Modal from '../../ui-elements/Modal/Modal';
import Link from '../../ui-elements/Link/Link';
import Alert from '../../ui-elements/Alert/Alert';
import { formatCategories } from './PepPage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faMinusCircle } from '@fortawesome/pro-solid-svg-icons';
// Type imports:
import { PepResult } from '../../types/PepTypes';
import { CountryList } from '../../assets/staticData/CountryList';

interface IProps {
    show: boolean;
    close(): void;
    data: PepResult[] | undefined;
}

const ForeignPepModal: React.FC<IProps> = ({ show, close, data }) => {
    const acceptedKeys: (keyof PepResult)[] = [
        'category',
        'categories',
        'matchRate',
        'match_rate',
        'matchedFields',
        'gender',
        'deceased',
        'deceasedDate',
        'firstName',
        'middleName',
        'lastName',
        'fullName',
        'images',
        'dateOfBirth',
        'locations',
        'nationalitiesIsoCodes'
    ];

    // Track expanded rows for multi-result table view
    const [openRows, setOpenRows] = useState<Set<number>>(new Set());
    const toggleRow = (idx: number) => {
        setOpenRows(prev => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx); else next.add(idx);
            return next;
        });
    };

    const renderValue = (value: any, key?: keyof PepResult) => {
        if (key === 'images') {
            if (Array.isArray(value) && value.length > 0) {
                return (
                    <div className="pep-image-links">
                        {value.map((img: string, i: number) => (
                            <div key={`${img}-${i}`}>
                                <Link targetBlank splitLines wrapTextForMobile url={img} linkSize="15">{img}</Link>
                            </div>
                        ))}
                    </div>
                );
            }
            return '-';
        }
        if (key === 'locations') {
            if (Array.isArray(value) && value.length > 0) {
                return value.map((loc: any, i: number) => (
                    <>
                        <div key={`${loc}-${i}`}>
                            {loc.address.length > 0 ? loc.address : CountryList.find(c => c.value === loc.countryCode)?.label ?? '-'}
                        </div>
                    </>
                ));
            }
            return '-';
        }
        if (key === 'nationalitiesIsoCodes') {
            return Array.isArray(value) ? value.map(code => CountryList.find(c => c.value === code)?.label ?? code).join(', ') : '-';
        }
        if (key === 'categories') return formatCategories(value);
        if (key === 'deceased') return value ? value.toString() : '-';
        if (key === 'matchedFields') return Array.isArray(value) ? value.join(', ') : value;
        return value === '' || value === null || value === undefined ? '-' : value;
    };

    const renderTitle = (key?: keyof PepResult) => {
        if (key === 'matchRate' || key === 'match_rate') return 'Match rate';
        if (key === 'fullName') return 'Full Name';
        if (key === 'dateOfBirth') return 'Date of Birth';
        if (key === 'nationalitiesIsoCodes') return 'Nationalities';
        if (key === 'matchedFields') return 'Matched Fields';
        if (key === 'deceasedDate') return 'Deceased Date';
        if (key === 'firstName') return 'First Name';
        if (key === 'middleName') return 'Middle Name';
        if (key === 'lastName') return 'Last Name';
        return key;
    };

    // Single item body (existing behavior)
    const renderSingle = (item: PepResult) => (
        <div>
            {item.resourceId &&
                <Link targetBlank linkSize="15" url={`/Pdf/Althjodlegir-valistar-einstaklingar-profile/${item.resourceId}`} icon="pdf">
                    Sækja skýrslu
                </Link>
            }
            <div className="KCL_foreignPepModal">
                {Object.keys(item).map((key, index) => {
                    if (acceptedKeys.includes(key as keyof PepResult)) {
                        const longValue = key === 'locations' || key === 'images' || key === 'categories';
                        return (
                            <div className="item_container" key={index}>
                                <span className={['item_key', longValue && 'long_value'].filter(Boolean).join(' ')}>{renderTitle(key as keyof PepResult)}</span>
                                <span className="item_value">
                                    {renderValue(item[key as keyof PepResult], key as keyof PepResult)}
                                </span>
                            </div>
                        );
                    }
                    return null;
                })}
            </div>
        </div>
    );

    // Collapsible list when multiple results
    const renderMultiple = (items: PepResult[]) => {
        // Table view with collapsible detail rows
        return (
            <div className="KCL_foreignPepModal-multi">
                <table className="pep-results-table">
                    <thead>
                        <tr>
                            <th></th>
                            <th>Match Rate</th>
                            <th>Nafn</th>
                            <th>Fæðingardagur</th>
                            <th>Þjóðerni</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, idx) => {
                            const isOpen = openRows.has(idx);
                            const name = item.fullName || [item.firstName, item.middleName, item.lastName].filter(Boolean).join(' ') || '-';
                            const dob = item.dateOfBirth || '-';
                            const country = item.nationalitiesIsoCodes?.join(', ') || item.generalInfo?.nationality || '-';
                            return (
                                <React.Fragment key={idx}>
                                    <tr
                                        className={isOpen ? 'is-open' : ''}
                                        onClick={() => toggleRow(idx)}
                                        role="button"
                                        aria-expanded={isOpen}
                                        aria-controls={`pep-row-details-${idx}`}
                                        tabIndex={0}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleRow(idx); } }}
                                    >
                                        <td>
                                            <button
                                                type="button"
                                                className="pep-row-toggle"
                                                aria-label={isOpen ? 'Loka' : 'Opna'}
                                            >
                                                <FontAwesomeIcon icon={isOpen ? faMinusCircle : faPlusCircle} />
                                            </button>
                                        </td>
                                        <td>{item.matchRate ?? item.match_rate ?? '-'}</td>
                                        <td>{name}</td>
                                        <td>{dob}</td>
                                        <td>{country}</td>
                                        <td>
                                            {item.resourceId &&
                                                <Link targetBlank linkSize="15" url={`/Pdf/Althjodlegir-valistar-einstaklingar-profile/${item.resourceId}`} icon="pdf">
                                                    Sækja skýrslu
                                                </Link>
                                            }
                                        </td>
                                    </tr>
                                    {isOpen && (
                                        <tr id={`pep-row-details-${idx}`} className="pep-details-row">
                                            <td colSpan={4}>
                                                <div className="KCL_foreignPepModal">
                                                    {Object.keys(item).map((key, index) => {
                                                        if (acceptedKeys.includes(key as keyof PepResult)) {
                                                            const longValue = key === 'locations' || key === 'images' || key === 'categories';
                                                            return (
                                                                <div className="item_container" key={index}>
                                                                    <span className={['item_key', longValue && 'long_value'].filter(Boolean).join(' ')}>{renderTitle(key as keyof PepResult)}</span>
                                                                    <span className="item_value">{renderValue(item[key as keyof PepResult], key as keyof PepResult)}</span>
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    })}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const modalBody = () => {
        if (!data || data.length === 0) {
            return <Alert type="alert" headText="Engin gögn fundust" />;
        }
        if (data.length === 1) return renderSingle(data[0]);
        return renderMultiple(data);
    };

    return (
        <Modal
            show={show}
            onHide={close}
            hasCloseButton
            header={
                !data || data.length === 0
                    ? '-'
                    : data.length === 1
                        ? data[0].fullName || '-'
                        : `Niðurstöður (${data.length})`
            }
            body={modalBody()}
        />
    );
};

export default ForeignPepModal;